// src/components/TrainerDashboard/PMCPane.jsx
import React, { useEffect, useMemo, useState, useLayoutEffect, useRef } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { useTranslation } from '../../i18n';

import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import * as am5exporting from "@amcharts/amcharts5/plugins/exporting";
import API_BASE_URL from '../../config/api';

const API = `${API_BASE_URL.replace(/\/+$/, '')}/api`;

// Devuelve claves de traducción en lugar de strings directos
const analyzeTSB = (todayTsb) => {
  if (todayTsb <= -20) return { levelKey: 'pmcPane.tsb.highRiskLevel', msgKey: 'pmcPane.tsb.highRiskMsg' };
  if (todayTsb <= -10) return { levelKey: 'pmcPane.tsb.tiredLevel',    msgKey: 'pmcPane.tsb.tiredMsg' };
  if (todayTsb <= +10) return { levelKey: 'pmcPane.tsb.optimalLevel',  msgKey: 'pmcPane.tsb.optimalMsg' };
  return               { levelKey: 'pmcPane.tsb.freshLevel',           msgKey: 'pmcPane.tsb.freshMsg' };
};

export default function PMCPane({ athleteId }) {
  const { t } = useTranslation();

  // ====== Estado base ======
  const [data, setData] = useState([]);
  const dataRef = useRef([]);
  const [loading, setLoading] = useState(false);

  const [daysRange, setDaysRange] = useState(90);
  const startISO = useMemo(() => dayjs().subtract(daysRange, 'day').startOf('day').toISOString(), [daysRange]);
  const endISO   = useMemo(() => dayjs().endOf('day').toISOString(), []);
  const today    = dayjs().format('YYYY-MM-DD');

  const [whatIf, setWhatIf] = useState({
    enabled: true, futureDays: 7, totalTss: 600,
    pattern: 'polarized', atlConst: 7, ctlConst: 42,
  });

  const [chartHeight, setChartHeight] = useState(() => Math.max(480, window.innerHeight - 260));
  useEffect(() => {
    const onResize = () => setChartHeight(Math.max(480, window.innerHeight - 260));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ====== KPIs ======
  const todayRow = useMemo(() => {
    const row = [...data].reverse().find(d => dayjs(d.date).format('YYYY-MM-DD') === today);
    return row || data[data.length - 1];
  }, [data, today]);

  const todayTsb = todayRow?.tsb ?? 0;
  const hint = analyzeTSB(todayTsb);

  const latest    = data?.[data.length - 1];
  const sevenDaysAgo = dayjs().subtract(7, 'day').startOf('day').valueOf();
  const closest7d = [...data].reverse().find(d => d.date <= dayjs().endOf('day').valueOf() && d.date >= sevenDaysAgo);
  const ctlToday  = latest?.ctl ?? null;
  const ctl7d     = closest7d?.ctl ?? null;
  const rampCTL   = (ctlToday != null && ctl7d != null) ? (ctlToday - ctl7d) : null;

  const rampFlag =
    rampCTL == null ? null :
    rampCTL > 8    ? { levelKey: 'pmcPane.ramp.highLevel',     msgKey: 'pmcPane.ramp.highMsg' } :
    rampCTL > 5    ? { levelKey: 'pmcPane.ramp.moderateLevel', msgKey: 'pmcPane.ramp.moderateMsg' } :
    rampCTL > 0    ? { levelKey: 'pmcPane.ramp.softLevel',     msgKey: 'pmcPane.ramp.softMsg' } :
                     { levelKey: 'pmcPane.ramp.declineLevel',  msgKey: 'pmcPane.ramp.declineMsg' };

  // ====== Fetch ======
  useEffect(() => {
    let alive = true;
    const fetchPMC = async () => {
      if (!athleteId) return;
      setLoading(true);
      try {
        await axios.post(`${API}/performance/recalculate/${athleteId}`, { start: startISO, end: endISO });
        const resp = await axios.get(`${API}/performance/pmc/${athleteId}`, { params: { start: startISO, end: endISO } });
        const rows = resp.data.map(r => ({
          date: new Date(r.date).getTime(),
          ctl: Number(r.ctl), atl: Number(r.atl), tsb: Number(r.tsb),
        }));
        if (alive) { setData(rows); dataRef.current = rows; }
      } catch (e) {
        console.error('❌ PMC fetch error:', e);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchPMC();
    return () => { alive = false; };
  }, [athleteId, startISO, endISO]);

  // ====== amCharts ======
  const chartDivRef = useRef(null);
  const axesRef   = useRef({ xAxis: null, yLeft: null, yRight: null, chart: null, root: null });
  const seriesRef = useRef({});

  useLayoutEffect(() => {
    if (!chartDivRef.current) return;

    const root = am5.Root.new(chartDivRef.current);
    root.setThemes([am5themes_Animated.new(root)]);
    root._logo?.dispose?.();

    const chart = root.container.children.push(am5xy.XYChart.new(root, {
      panX: true, panY: false, wheelX: "panX", wheelY: "zoomX",
      layout: root.verticalLayout
    }));

    const xAxis = chart.xAxes.push(am5xy.DateAxis.new(root, {
      baseInterval: { timeUnit: "day", count: 1 },
      renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 55, grid: { strokeOpacity: 0.08 } }),
      tooltip: am5.Tooltip.new(root, { forceHidden: true })
    }));

    const yLeft = chart.yAxes.push(am5xy.ValueAxis.new(root, {
      renderer: am5xy.AxisRendererY.new(root, { grid: { strokeOpacity: 0.08 } }),
      min: 0, strictMinMax: false, numberFormat: "#.#", extraMax: 0.08
    }));
    yLeft.children.unshift(am5.Label.new(root, {
      rotation: -90, text: t('pmcPane.yLeftLabel'),
      centerY: am5.p50, centerX: am5.p50, y: am5.p50, x: -35, opacity: 0.7, fontSize: 11
    }));

    const yRight = chart.yAxes.push(am5xy.ValueAxis.new(root, {
      renderer: am5xy.AxisRendererY.new(root, { opposite: true, grid: { strokeOpacity: 0.08 } }),
      min: -50, max: 50, strictMinMax: true, numberFormat: "#.#"
    }));
    yRight.children.push(am5.Label.new(root, {
      rotation: 90, text: t('pmcPane.yRightLabel'),
      centerY: am5.p50, centerX: am5.p50, y: am5.p50, x: 35, opacity: 0.7, fontSize: 11
    }));

    const r0 = yRight.createAxisRange(yRight.makeDataItem({ value: 0 }));
    r0.get("grid").setAll({ strokeOpacity: 0.6, strokeDasharray: [3, 3] });
    r0.get("label").setAll({ text: "0", opacity: 0.6, fontSize: 10, dx: -4 });

    const makeTSBBand = (from, to, color, opacity = 0.06, label) => {
      const item  = yRight.makeDataItem({ value: from, endValue: to });
      const range = yRight.createAxisRange(item);
      range.get("axisFill").setAll({ fill: am5.color(color), fillOpacity: opacity });
      range.get("grid").setAll({ strokeOpacity: 0 });
      if (label) range.get("label").setAll({ text: label, dy: -2, opacity: 0.75, fontSize: 10, fill: am5.color(color) });
    };
    makeTSBBand(-1000, -20, "#ef4444", 0.08, "TSB ≤ -20 (riesgo)");
    makeTSBBand(-20,  -10,  "#f59e0b", 0.07, "-20 … -10");
    makeTSBBand(-10,   10,  "#22c55e", 0.06, "-10 … +10");
    makeTSBBand( 10, 1000,  "#3b82f6", 0.05, "≥ +10");

    chart.set("cursor", am5xy.XYCursor.new(root, { xAxis }));
    chart.set("scrollbarX", am5.Scrollbar.new(root, { orientation: "horizontal" }));

    const masterTT = am5.Tooltip.new(root, { getFillFromSprite: false });
    masterTT.get("background").setAll({ fillOpacity: 0.96 });
    chart.set("tooltip", masterTT);
    const nearest = (arr, x) => {
      let best = null, d = Infinity;
      for (const r of arr) { const dd = Math.abs(r.date - x); if (dd < d) { d = dd; best = r; } }
      return best;
    };
    chart.events.on("pointerpositionchanged", () => {
      const pos = chart.get("cursor")?.getPrivate("positionX");
      if (pos == null) return;
      const dateVal = xAxis.positionToValue(pos);
      const row = nearest(dataRef.current || [], dateVal);
      if (!row) return;
      masterTT.label.set("text",
        `${new Date(row.date).toDateString()}\n` +
        `CTL: ${Number(row.ctl).toFixed(1)}  •  ` +
        `ATL: ${Number(row.atl).toFixed(1)}  •  ` +
        `TSB: ${Number(row.tsb).toFixed(1)}`
      );
    });

    const createSeries = (name, field, color, yAxis, dashed = false, fillUnder = false) => {
      const s = chart.series.push(am5xy.LineSeries.new(root, {
        name, xAxis, yAxis, valueXField: "date", valueYField: field,
        tooltip: am5.Tooltip.new(root, { forceHidden: true })
      }));
      s.strokes.template.setAll({ strokeWidth: 2.5, stroke: am5.color(color), opacity: 1 });
      if (dashed) s.strokes.template.setAll({ strokeDasharray: [7, 5] });
      if (fillUnder) s.fills.template.setAll({ visible: true, fillOpacity: 0.08, fill: am5.color(color) });
      return s;
    };

    const CTL_COLOR = "#1f77b4";
    const ATL_COLOR = "#ff7f0e";
    const TSB_COLOR = "#9467bd";

    const sCTL     = createSeries(t('pmcPane.seriesFitness'),  "ctl", CTL_COLOR, yLeft, false, true);
    const sATL     = createSeries(t('pmcPane.seriesFatigue'),  "atl", ATL_COLOR, yLeft);
    const sTSB     = createSeries(t('pmcPane.seriesForm'),     "tsb", TSB_COLOR, yRight);
    const sCTLProj = createSeries(t('pmcPane.seriesCtlProj'),  "ctl", CTL_COLOR, yLeft, true);
    const sATLProj = createSeries(t('pmcPane.seriesAtlProj'),  "atl", ATL_COLOR, yLeft, true);
    const sTSBProj = createSeries(t('pmcPane.seriesTsbProj'),  "tsb", TSB_COLOR, yRight, true);

    const addSeriesBadge = (series) => {
      const label = chart.plotContainer.children.push(am5.Label.new(root, {
        text: series.get("name"),
        x: am5.p100, centerX: am5.p100, y: 0, centerY: 0, opacity: 0.85, fontSize: 11,
        background: am5.RoundedRectangle.new(root, {
          fill: series.get("stroke"), fillOpacity: 0.12, strokeOpacity: 0,
          cornerRadiusTL: 6, cornerRadiusTR: 6, cornerRadiusBL: 6, cornerRadiusBR: 6
        }),
        paddingTop: 4, paddingBottom: 4, paddingLeft: 6, paddingRight: 6
      }));
      const update = () => {
        const last = series.dataItems[series.dataItems.length - 1];
        if (!last) return;
        const yVal = Number(last.get("valueY"));
        const pos  = series.get("yAxis").valueToPosition(yVal) * chart.plotContainer.height();
        label.setAll({ y: pos });
        label.set("text", `${series.get("name")} ${isFinite(yVal) ? yVal.toFixed(1) : "-"}`);
      };
      series.events.on("datavalidated", update);
      series.events.on("boundschanged", update);
      update();
    };
    addSeriesBadge(sCTL); addSeriesBadge(sATL); addSeriesBadge(sTSB);

    seriesRef.current = { ctl: sCTL, atl: sATL, tsb: sTSB, ctlProj: sCTLProj, atlProj: sATLProj, tsbProj: sTSBProj };
    axesRef.current   = { xAxis, yLeft, yRight, chart, root };

    const legend = chart.children.push(am5.Legend.new(root, { useDefaultMarker: true }));
    legend.data.setAll([sCTL, sATL, sTSB, sCTLProj, sATLProj, sTSBProj]);

    am5exporting.Exporting.new(root, {
      menu: am5exporting.ExportingMenu.new(root, {}),
      filePrefix: "banister-ctl-atl-tsb"
    });

    return () => { root.dispose(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== what-if =====
  function buildDistribution(total, days, pattern) {
    if (days <= 0 || total <= 0) return Array.from({ length: Math.max(0, days) }, () => 0);
    if (pattern === 'even' || days !== 7) {
      const each = total / days;
      return Array.from({ length: days }, () => each);
    }
    const weights = [0.40, 0.25, 0.00, 0.20, 0.15, 0.00, 0.00];
    const sum = weights.reduce((a, b) => a + b, 0);
    return weights.map(w => (w / sum) * total);
  }

  function projectPMC(lastPoint, arrTss, atlConst, ctlConst) {
    if (!lastPoint) return [];
    let { date, atl, ctl } = lastPoint;
    const out = [];
    for (let i = 0; i < arrTss.length; i++) {
      const tss = Number(arrTss[i] || 0);
      date = dayjs(date).add(1, 'day').valueOf();
      atl  = atl + (tss - atl) / atlConst;
      ctl  = ctl + (tss - ctl) / ctlConst;
      out.push({ date, atl, ctl, tsb: ctl - atl });
    }
    return out;
  }

  const [projData, setProjData] = useState([]);

  useEffect(() => {
    const { ctl, atl, tsb, ctlProj, atlProj, tsbProj } = seriesRef.current || {};
    if (!ctl || !atl || !tsb) return;

    ctl.data.setAll(data); atl.data.setAll(data); tsb.data.setAll(data);
    dataRef.current = data;

    if (axesRef.current?.yRight && data.length) {
      const minTSB = Math.min(...data.map(d => d.tsb), -40);
      const maxTSB = Math.max(...data.map(d => d.tsb),  40);
      const absMax = Math.max(Math.abs(minTSB), Math.abs(maxTSB), 30);
      axesRef.current.yRight.setAll({ min: -Math.ceil(absMax/10)*10, max: Math.ceil(absMax/10)*10, strictMinMax: true });
    }

    if (whatIf.enabled && data.length) {
      const last = data[data.length - 1];
      const dist = buildDistribution(whatIf.totalTss, whatIf.futureDays, whatIf.pattern);
      const sim  = projectPMC({ ...last }, dist, whatIf.atlConst, whatIf.ctlConst);
      setProjData(sim);
      if (ctlProj && atlProj && tsbProj) {
        ctlProj.data.setAll(sim); atlProj.data.setAll(sim); tsbProj.data.setAll(sim);
      }
    } else {
      setProjData([]);
      if (seriesRef.current.ctlProj) {
        seriesRef.current.ctlProj.data.setAll([]);
        seriesRef.current.atlProj.data.setAll([]);
        seriesRef.current.tsbProj.data.setAll([]);
      }
    }
  }, [data, whatIf]);

  const tsbProjectedEnd = projData.length ? projData[projData.length - 1].tsb : null;

  // ===== UI =====
  return (
    <div className="card p-3" style={{ width: '100%', maxWidth: '100%' }}>
      <h5 className="mb-2">{t('pmcPane.title')}</h5>

      <div className="d-flex flex-wrap gap-3 align-items-end mb-3">
        <div>
          <label className="form-label mb-1"><strong>{t('pmcPane.range')}</strong></label><br/>
          <div className="btn-group" role="group" aria-label={t('pmcPane.rangeDaysAriaLabel')}>
            {[30, 60, 90, 120].map(d => (
              <button key={d}
                className={`btn btn-sm ${daysRange === d ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setDaysRange(d)}
              >{d}d</button>
            ))}
          </div>
        </div>

        <div className="form-check form-switch ms-2">
          <input className="form-check-input" type="checkbox" id="whatIfSwitch"
                 checked={whatIf.enabled} onChange={e => setWhatIf(w => ({ ...w, enabled: e.target.checked }))}/>
          <label className="form-check-label" htmlFor="whatIfSwitch">
            <strong>{t('pmcPane.whatIf')}</strong>
          </label>
        </div>

        <div>
          <label className="form-label mb-1">{t('pmcPane.days')}</label>
          <input type="number" min={1} max={14} className="form-control form-control-sm"
                 value={whatIf.futureDays}
                 onChange={e => setWhatIf(w => ({ ...w, futureDays: Math.max(1, Math.min(14, Number(e.target.value)||1)) }))}/>
        </div>

        <div style={{ minWidth: 220 }}>
          <label className="form-label mb-1">{t('pmcPane.totalTss')}</label>
          <input type="range" min={0} max={1500} step={10} className="form-range"
                 value={whatIf.totalTss}
                 onChange={e => setWhatIf(w => ({ ...w, totalTss: Number(e.target.value) }))}/>
          <div style={{ fontSize: 12 }}>{whatIf.totalTss} TSS</div>
        </div>

        <div>
          <label className="form-label mb-1">{t('pmcPane.pattern')}</label>
          <select className="form-select form-select-sm" value={whatIf.pattern}
                  onChange={e => setWhatIf(w => ({ ...w, pattern: e.target.value }))}>
            <option value="even">{t('pmcPane.patternEven')}</option>
            <option value="polarized">{t('pmcPane.patternPolarized')}</option>
          </select>
        </div>
      </div>

      {/* KPIs */}
      <div className="row g-2 mb-2">
        <div className="col-12 col-md-3">
          <div className="p-2 border rounded">
            <div><strong>{t('pmcPane.todayTsb')}</strong></div>
            <div style={{ fontSize: 22 }}>{Number.isFinite(todayTsb) ? todayTsb.toFixed(2) : '-'}</div>
            <small className="text-muted">{t(hint.levelKey)}: {t(hint.msgKey)}</small>
          </div>
        </div>
        <div className="col-12 col-md-3">
          <div className="p-2 border rounded">
            <div><strong>{t('pmcPane.ctlToday')}</strong></div>
            <div style={{ fontSize: 22 }}>{ctlToday != null ? ctlToday.toFixed(1) : '-'}</div>
            <small className="text-muted">ΔCTL 7d: {rampCTL != null ? (rampCTL >= 0 ? '+' : '') + rampCTL.toFixed(1) : '-'}</small><br/>
            <small className="text-muted">{rampFlag ? `${t(rampFlag.levelKey)}: ${t(rampFlag.msgKey)}` : ''}</small>
          </div>
        </div>
        <div className="col-12 col-md-6">
          <div className="p-2 border rounded">
            <div><strong>{t('pmcPane.whatIfLabel')}</strong></div>
            <div style={{ fontSize: 16 }}>
              {whatIf.enabled
                ? t('pmcPane.whatIfProjection', { days: whatIf.futureDays, tsb: tsbProjectedEnd != null ? tsbProjectedEnd.toFixed(1) : '-', tss: whatIf.totalTss, pattern: whatIf.pattern })
                : t('pmcPane.whatIfDisabled')}
            </div>
            <small className="text-muted">{t('pmcPane.whatIfHint')}</small>
          </div>
        </div>
      </div>

      {loading && <p>{t('pmcPane.loading')}</p>}
      {!loading && (!data || data.length === 0) && <p>{t('pmcPane.noData')}</p>}

      <div ref={chartDivRef} style={{ width: '100%', height: chartHeight }} aria-label={t('pmcPane.chartAriaLabel')} />

      <small className="text-muted d-block mt-2">{t('pmcPane.chartFootnote')}</small>
    </div>
  );
}
